import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Check, ChevronDown, ChevronUp, Trash2, X } from 'lucide-react';
import { SaleInstalment } from '../../api/SalesAPI';
import { INSTALMENT_SUM_EPS } from '../../utils/saleInstalmentSuggestions';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../Collapsible/Collapsible';
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';
import IconButton from '../IconButton/IconButton';
import InputNumber from '../InputNumber/InputNumber';
import InputShortText from '../InputShortText/InputShortText';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import SecondaryButton from '../SecondaryButton/SecondaryButton';
import SelectInput from '../SelectInput/SelectInput';
import TextOptionalActionButton from '../TextOptionalActionButton/TextOptionalActionButton';
import Tooltip from '../Tooltip/Tooltip';

type PaymentMethodOption = {
  value: string;
  readableValue: string;
};

type Props = {
  index: number;
  instalment: SaleInstalment;
  currency: string;
  paymentMethods: PaymentMethodOption[];
  timeZone: string;
  isDisabled?: boolean;
  showDueDate: boolean;
  showInvoice: boolean;
  suggestedAmount?: number;
  suggestedCurrency?: string;
  suggestionDismissed?: boolean;
  onChange: (patch: Partial<SaleInstalment>) => void;
  onMarkPaid: () => void;
  onMarkUnpaid: () => void;
  onRemove: () => void;
  onToggleDueDate: () => void;
  onToggleInvoice: () => void;
  onApplySuggestion: () => void;
  onDismissSuggestion: () => void;
};

function SaleInstalmentCard({
  index,
  instalment,
  currency,
  paymentMethods,
  timeZone,
  isDisabled = false,
  showDueDate,
  showInvoice,
  suggestedAmount,
  suggestedCurrency,
  suggestionDismissed = false,
  onChange,
  onMarkPaid,
  onMarkUnpaid,
  onRemove,
  onToggleDueDate,
  onToggleInvoice,
  onApplySuggestion,
  onDismissSuggestion,
}: Props) {
  const { t } = useTranslation();

  const curAmt = instalment.amount ?? 0;
  const hasVisibleSuggestion =
    suggestedAmount != null &&
    !suggestionDismissed &&
    Math.abs(curAmt - suggestedAmount) >= INSTALMENT_SUM_EPS;

  const [open, setOpen] = useState(hasVisibleSuggestion);

  useEffect(() => {
    if (hasVisibleSuggestion) {
      setOpen(true);
    }
  }, [hasVisibleSuggestion]);

  const methodLabel =
    paymentMethods.find((m) => m.value === (instalment.paymentMethod || 'TRANSFER'))
      ?.readableValue ||
    instalment.paymentMethod ||
    'TRANSFER';

  const summaryParts = [
    `${(instalment.amount ?? 0).toLocaleString()} ${currency}`,
    methodLabel,
  ];
  if (instalment.dueDate) {
    summaryParts.push(
      `${t('payment_plan.due_date') || 'Due'}: ${instalment.dueDate}`
    );
  }

  const title = `${t('sales.form.instalment') || 'Instalment'} ${index + 1}`;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200/80"
    >
      <div className="flex items-start justify-between gap-2 p-4 pb-3">
        <CollapsibleTrigger
          data-testid={`instalment-card-${index}`}
          className="flex min-w-0 flex-1 items-start gap-2 text-left hover:opacity-80"
          aria-label={`${title}. ${t('sales.form.instalment.toggle_details') || 'Show instalment details'}`}
        >
          {open ? (
            <ChevronUp className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden />
          ) : (
            <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-gray-900">{title}</h4>
            <p className="mt-0.5 text-sm text-gray-600 truncate">{summaryParts.join(' · ')}</p>
          </div>
        </CollapsibleTrigger>
        <div className="flex shrink-0 items-center space-x-2">
          {!instalment.isPaid && !isDisabled && (
            <Tooltip content={t('payment_plan.mark_as_paid') || 'Mark as paid'} position="bottom">
              <button
                type="button"
                onClick={onMarkPaid}
                className="flex items-center space-x-1 rounded-full px-2 py-1 transition-colors hover:bg-green-200 group"
              >
                <Check className="h-4 w-4 text-green-600 group-hover:text-green-700" />
                <span className="hidden text-xs text-green-600 group-hover:text-green-700 sm:inline">
                  {t('payment_plan.mark_as_paid')}
                </span>
              </button>
            </Tooltip>
          )}
          {instalment.isPaid && (
            <div className="flex items-center space-x-2">
              <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                {t('payment_plan.paid') || 'Paid'}
              </span>
              {!isDisabled && (
                <Tooltip content={t('payment_plan.mark_as_unpaid') || 'Mark as unpaid'} position="bottom">
                  <button
                    type="button"
                    onClick={onMarkUnpaid}
                    className="rounded-full p-1 transition-colors hover:bg-red-200 group"
                  >
                    <X className="h-4 w-4 text-red-600 group-hover:text-red-700" />
                  </button>
                </Tooltip>
              )}
            </div>
          )}
          {!isDisabled && (
            <Tooltip content={t('payment_plan.remove_instalment') || 'Remove payment instalment'} position="bottom">
              <IconButton actionName="Remove Instalment" onClick={onRemove}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
      <CollapsibleContent>
        {open ? (
          <div className="space-y-3 border-t border-gray-200 px-4 pb-4 pt-3">
            <div className="flex space-x-3">
              <div className="flex-1">
                <InputNumber
                  label={t('sales.form.instalment.amount') || 'Amount'}
                  onChange={(e) => onChange({ amount: Number(e.target.value) || 0 })}
                  defaultValue={instalment.amount}
                  isDisabled={isDisabled}
                  validationFn={(value) => value >= 0}
                  errorMessage="errors.minimum_zero"
                />
              </div>
              <div className="flex-1">
                <SelectInput
                  label={t('sales.form.instalment.method') || 'Payment Method'}
                  values={paymentMethods}
                  onChange={(e) =>
                    onChange({
                      paymentMethod: e.target.value as SaleInstalment['paymentMethod'],
                    })
                  }
                  defaultValue={instalment.paymentMethod || 'TRANSFER'}
                  isDisabled={isDisabled}
                />
              </div>
            </div>

            {hasVisibleSuggestion && suggestedAmount != null && (
              <div
                data-testid={`instalment-suggestion-row-${index}`}
                className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/90 p-3"
              >
                <p className="text-sm text-gray-800">
                  {t('sales.form.instalment_suggestion.row_suggested', {
                    amount: suggestedAmount.toLocaleString(),
                    currency: suggestedCurrency || currency,
                  })}
                </p>
                <div className="flex flex-wrap gap-2">
                  <PrimaryButton
                    text={String(t('sales.form.instalment_suggestion.apply_row'))}
                    fn={onApplySuggestion}
                    isDisabled={isDisabled}
                    actionName={`Apply instalment ${index + 1} suggestion`}
                  />
                  <SecondaryButton
                    text={String(t('sales.form.instalment_suggestion.dismiss_row'))}
                    fn={onDismissSuggestion}
                    isDisabled={isDisabled}
                    actionName={`Dismiss instalment ${index + 1} suggestion`}
                  />
                </div>
              </div>
            )}

            <InputShortText
              label={t('sales.form.instalment.amount_in_words') || 'Amount in Words'}
              defaultValue={instalment.amountInWords || ''}
              onChange={(e) => onChange({ amountInWords: e.target.value })}
              isDisabled={isDisabled}
            />

            {!showDueDate && !instalment.dueDate ? (
              <TextOptionalActionButton
                text={t('payment_plan.assign_due_date') || 'Assign a due date'}
                onClick={onToggleDueDate}
                disabled={isDisabled}
                actionName="Assign Due Date"
                icon={<Calendar className="h-4 w-4" />}
                variant="blue"
              />
            ) : (
              <div>
                <CustomDatePicker
                  label={t('payment_plan.due_date') || 'Due Date'}
                  value={instalment.dueDate ? new Date(instalment.dueDate) : null}
                  onChange={(date) => {
                    onChange({
                      dueDate: date ? date.toISOString().split('T')[0] : undefined,
                    });
                    if (!date) {
                      onToggleDueDate();
                    }
                  }}
                  isDisabled={isDisabled}
                  timeZone={timeZone}
                  disablePast={false}
                />
              </div>
            )}

            {instalment.isPaid && (
              <div>
                <CustomDatePicker
                  label={t('payment_plan.paid_on') || 'Paid on'}
                  value={instalment.paidDate ? new Date(instalment.paidDate) : new Date()}
                  onChange={(date) =>
                    onChange({ paidDate: date?.toISOString().split('T')[0] })
                  }
                  isDisabled={isDisabled}
                  timeZone={timeZone}
                  disablePast={false}
                />
              </div>
            )}

            {instalment.isPaid && (
              <div>
                {!showInvoice && !instalment.invoiceReference ? (
                  <TextOptionalActionButton
                    text={t('payment_plan.add_invoice_reference') || 'Add receipt reference'}
                    onClick={onToggleInvoice}
                    disabled={isDisabled}
                    actionName="Add Receipt Reference"
                    icon={null}
                    variant="blue"
                  />
                ) : (
                  <div>
                    <InputShortText
                      label={t('payment_plan.invoice_reference') || 'Receipt Reference'}
                      onChange={(e) => onChange({ invoiceReference: e.target.value })}
                      defaultValue={instalment.invoiceReference || ''}
                      isDisabled={isDisabled}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}

export default SaleInstalmentCard;
